import { Commodity, Unit } from '@enmon/commons';
import {
  DiameterNominal,
  ElectricityDeviceCategory,
  ElectricityMeterMeasurementType,
  GasDeviceCategory,
  ThermalMeasurementType,
  InstallationPosition,
  Mounting,
  Orientation,
  OtherDeviceCategory,
  Pmax,
  Resistance,
  TubeMaterial,
  WaterMeterMeasurementType,
} from '@enmon/commons/lib/shared/meter';
import { InventoryMeterMountType } from '@enmon/commons/lib/tools';
import { MeterAccessibility, MeterOwnerType } from '@enmon/commons/lib/enmon';
import { COMMODITY_CONFIG } from 'consts/commodities';
import {
  ElectricityDeviceCategory as electricityDeviceCategoryMessages,
  ElectricityMeasurementType as electricityMeasurementTypeMessages,
  GasDeviceCategory as gasDeviceCategoryMessages,
  InstallationPosition as installationPositionMessages,
  MeterAccessibility as meterAccessibilityMessages,
  InventoryMeterMountType as inventoryMeterMountTypeMessages,
  MeterOwnerType as meterOwnerTypeMessages,
  Mounting as mountingMessages,
  Orientation as orientationMessages,
  ThermalMeasurementType as thermalMeasurementTypeMessages,
  OtherDeviceCategory as otherDeviceCategoryMessages,
  TubeMaterial as tubeMaterialMessages,
  WaterMeasurementType as waterMeterMeasurementTypeMessages,
} from './messages';

export const deviceCategoryTranslated = {
  [OtherDeviceCategory.Gateway]: otherDeviceCategoryMessages.gateway,
  [OtherDeviceCategory.LoRaGateway]: otherDeviceCategoryMessages.loRaGateway,
  [OtherDeviceCategory.LoRaGatewayPowerSupply]: otherDeviceCategoryMessages.loRaGatewayPowerSupply,
  [OtherDeviceCategory.Thermometer]: otherDeviceCategoryMessages.thermometer,
  [ElectricityDeviceCategory.Basic]: electricityDeviceCategoryMessages.basic,
  [ElectricityDeviceCategory.FourQuadrants]: electricityDeviceCategoryMessages.fourQuadrants,
  [ElectricityDeviceCategory.Multimeter]: electricityDeviceCategoryMessages.multimeter,
  [ElectricityDeviceCategory.SmartCircuitBreaker]: electricityDeviceCategoryMessages.smartCircuitBreaker,
  [GasDeviceCategory.Rotary]: gasDeviceCategoryMessages.rotary,
  [GasDeviceCategory.Membrane]: gasDeviceCategoryMessages.membrane,
  [GasDeviceCategory.Turbine]: gasDeviceCategoryMessages.turbine,
  [GasDeviceCategory.Quantometer]: gasDeviceCategoryMessages.quantometer,
};

export const electricityMeterMeasurementTypeTranslated = {
  [ElectricityMeterMeasurementType.Inductive]: electricityMeasurementTypeMessages.inductive,
  [ElectricityMeterMeasurementType.Static]: electricityMeasurementTypeMessages.static,
};

export const waterMeterMeasurementTypeTranslated = {
  [WaterMeterMeasurementType.Mechanical]: waterMeterMeasurementTypeMessages.mechanical,
  [WaterMeterMeasurementType.Static]: waterMeterMeasurementTypeMessages.static,
};

export const mountingTranslated = {
  [Mounting.DIN]: mountingMessages.din,
  [Mounting.Cross]: mountingMessages.cross,
  [Mounting.Panel]: mountingMessages.panel,
};

export const accessibilityTranslated = {
  [MeterAccessibility.Good]: meterAccessibilityMessages.good,
  [MeterAccessibility.Tenant]: meterAccessibilityMessages.tenant,
  [MeterAccessibility.Basement]: meterAccessibilityMessages.basement,
  [MeterAccessibility.Ceiling]: meterAccessibilityMessages.ceiling,
  [MeterAccessibility.Shaft]: meterAccessibilityMessages.shaft,
  [MeterAccessibility.FloodedShaft]: meterAccessibilityMessages.floodedShaft,
  [MeterAccessibility.High]: meterAccessibilityMessages.high,
  [MeterAccessibility.FairlyHigh]: meterAccessibilityMessages.fairlyHigh,
  [MeterAccessibility.VeryHigh]: meterAccessibilityMessages.veryHigh,
  [MeterAccessibility.Bad]: meterAccessibilityMessages.bad,
};

export const ownerTypeTranslated = {
  [MeterOwnerType.Client]: meterOwnerTypeMessages.client,
  [MeterOwnerType.Tenant]: meterOwnerTypeMessages.tenant,
  [MeterOwnerType.UtilityCompany]: meterOwnerTypeMessages.utilityCompany,
  [MeterOwnerType.Lessor]: meterOwnerTypeMessages.lessor,
};

export const diameterNominalTranslated = {
  [DiameterNominal.DN15]: 'DN15 | 1/2” | 21 mm',
  [DiameterNominal.DN20]: 'DN20 | 3/4” | 26.4 mm',
  [DiameterNominal.DN25]: 'DN25 | 1” | 33.3 mm',
  [DiameterNominal.DN32]: 'DN32 | 1-1/4” | 41.9 mm',
  [DiameterNominal.DN40]: 'DN40 | 1-1/2” | 47.8 mm',
  [DiameterNominal.DN50]: 'DN50 | 2” | 59.6 mm',
  [DiameterNominal.DN65]: 'DN65 | 2-1/2” | 73 mm',
  [DiameterNominal.DN80]: 'DN80 | 3” | 88.9 mm',
  [DiameterNominal.DN100]: 'DN100 | 4” | 114.3 mm',
  [DiameterNominal.DN125]: 'DN125 | 5” | 141.3 mm',
  [DiameterNominal.DN150]: 'DN150 | 6” | 168.3 mm',
  [DiameterNominal.DN200]: 'DN200 | 8” | 219.1 mm',
};

export const pMaxTranslated = {
  [Pmax['0.02_MPA']]: 'Pmax 0.02 Mpa | 0.2 bar',
  [Pmax['0.03_MPA']]: 'Pmax 0.03 Mpa | 0.3 bar',
  [Pmax['0.05_MPA']]: 'Pmax 0.05 Mpa | 0.5 bar',
  [Pmax['0.1_MPA']]: 'Pmax 0.1 Mpa | 1 bar',
  [Pmax['0.2_MPA']]: 'Pmax 0.2 Mpa | 2 bar',
  [Pmax['0.5_MPA']]: 'Pmax 0.5 Mpa | 5 bar',
  [Pmax['1_MPA']]: 'Pmax 1 Mpa | 10 bar',
  [Pmax['1.2_MPA']]: 'Pmax 1.2 Mpa | 12 bar',
  [Pmax['1.6_MPA']]: 'Pmax 1.6 Mpa | 16 bar',
  [Pmax['2_MPA']]: 'Pmax 2 Mpa | 20 bar',
  [Pmax['4_MPA']]: 'Pmax 4 Mpa | 40 bar',
  [Pmax['6_MPA']]: 'Pmax 6 Mpa | 60 bar',
  [Pmax['10_MPA']]: 'Pmax 10 Mpa | 100 bar',
  [Pmax['12_MPA']]: 'Pmax 12 Mpa | 120 bar',
  [Pmax['16_MPA']]: 'Pmax 16 Mpa | 160 bar',
  [Pmax['20_MPA']]: 'Pmax 20 Mpa | 200 bar',
  [Pmax['21_MPA']]: 'Pmax 21 Mpa | 210 bar',
  [Pmax['25_MPA']]: 'Pmax 25 Mpa | 250 bar',
  [Pmax['40_MPA']]: 'Pmax 40 Mpa | 400 bar',
  [Pmax['50_MPA']]: 'Pmax 50 Mpa | 500 bar',
  [Pmax['63_MPA']]: 'Pmax 63 Mpa | 630 bar',
  [Pmax['64_MPA']]: 'Pmax 64 Mpa | 640 bar',
  [Pmax['100_MPA']]: 'Pmax 100 Mpa | 1000 bar',
} satisfies Record<Pmax, string>;

export const tubeMaterialTranslated = {
  [TubeMaterial.Copper]: tubeMaterialMessages.copper,
  [TubeMaterial.Plastic]: tubeMaterialMessages.plastic,
  [TubeMaterial.Steel]: tubeMaterialMessages.steel,
};

export const installationPositionTranslated = {
  [InstallationPosition.Forward]: installationPositionMessages.forward,
  [InstallationPosition.Return]: installationPositionMessages.return,
};

export const meterMountTypeTranslated = {
  [InventoryMeterMountType.Free]: inventoryMeterMountTypeMessages.free,
  [InventoryMeterMountType.MountedFree]: inventoryMeterMountTypeMessages.mountedFree,
  [InventoryMeterMountType.MountedOccupied]: inventoryMeterMountTypeMessages.mountedOccupied,
};

export const commodityTranslated = {
  [Commodity.Electricity]: COMMODITY_CONFIG[Commodity.Electricity].message,
  [Commodity.Water]: COMMODITY_CONFIG[Commodity.Water].message,
  [Commodity.NaturalGas]: COMMODITY_CONFIG[Commodity.NaturalGas].message,
  [Commodity.Thermal]: COMMODITY_CONFIG[Commodity.Thermal].message,
  [Commodity.Other]: COMMODITY_CONFIG[Commodity.Other].message,
};

export const commodityDefaultUnit = {
  [Commodity.Electricity]: Unit.kWh,
  [Commodity.Water]: Unit.m3,
  [Commodity.NaturalGas]: Unit.m3,
  [Commodity.Thermal]: Unit.GJ,
  [Commodity.Other]: Unit.GJ,
};

export const orientationTranslated = {
  [Orientation.Horizontal]: orientationMessages.horizontal,
  [Orientation.VerticalFlowUp]: orientationMessages.verticalFlowUp,
  [Orientation.VerticalFlowDown]: orientationMessages.verticalFlowDown,
};

export const thermalMeasurementTypeTranslated = {
  [ThermalMeasurementType.Heat]: thermalMeasurementTypeMessages.heat,
  [ThermalMeasurementType.Cold]: thermalMeasurementTypeMessages.cold,
  [ThermalMeasurementType.Both]: thermalMeasurementTypeMessages.both,
};

export const resistanceTranslated = {
  [Resistance.Pt100]: 'Pt 100',
  [Resistance.Pt500]: 'Pt 500',
  [Resistance.Pt1000]: 'Pt 1000',
};

export enum FlowrateQp {
  'Qp-0.6' = 0.6,
  'Qp-1.5' = 1.5,
  'Qp-2.5' = 2.5,
  'Qp-3.5' = 3.5,
  'Qp-6' = 6,
  'Qp-10' = 10,
  'Qp-15' = 15,
  'Qp-25' = 25,
  'Qp-30' = 30,
  'Qp-40' = 40,
  'Qp-60' = 60,
  'Qp-100' = 100,
  'Qp-150' = 150,
  'Qp-250' = 250,
}

export enum FlowrateQ3 {
  'Q3-1.6' = 1.6,
  'Q3-2.5' = 2.5,
  'Q3-4' = 4,
  'Q3-6.3' = 6.3,
  'Q3-10' = 10,
  'Q3-16' = 16,
  'Q3-25' = 25,
  'Q3-40' = 40,
  'Q3-63' = 63,
  'Q3-100' = 100,
  'Q3-160' = 160,
  'Q3-260' = 260,
  'Q3-400' = 400,
  'Q3-630' = 630,
  'Q3-1000' = 1000,
}

export enum FlowrateQn {
  'Qn-1.6' = 1.6,
  'Qn-2.5' = 2.5,
  'Qn-3.5' = 3.5,
  'Qn-6' = 6,
  'Qn-10' = 10,
  'Qn-15' = 15,
  'Qn-25' = 25,
  'Qn-40' = 40,
  'Qn-60' = 60,
  'Qn-100' = 100,
  'Qn-150' = 150,
  'Qn-200' = 200,
}
